import React from 'react';

import { MDText } from 'i18n-react';

import { ServiceFeeAppliesNotice } from 'Components/transactionFees/ServiceFeeAppliesNotice/ServiceFeeAppliesNotice';
import { useVehicleCollectionSub } from 'Containers/app/vehicleCollection/hooks/useVehicleCollectionSub';
import { VehicleCard, VehicleCardProps } from 'Storybook/components/local/VehicleCard';

import { useOfferSummary } from '../hooks/useOfferSummary';

import LocalTexts from './ServiceFeeSummary.texts.json';

import styles from './ServiceFeeSummary.styles.scss';

const LocalT = new MDText(LocalTexts);

type ServiceFeeSummaryProps = Omit<VehicleCardProps, 'vrm'> & {
  vrmPretty?: string;
  withVehicleCard?: boolean;
};

export const ServiceFeeSummary: React.FC<ServiceFeeSummaryProps> = ({
  badge,
  image,
  showVehicleInfo,
  vehicleDetails,
  vrmPretty,
  withVehicleCard = true,
}) => {
  const { offer } = useOfferSummary();
  const { serviceFee, serviceFeeTiers } = useVehicleCollectionSub();

  const soldFor = offer.acceptedBidValue;
  const feeAmountGBP = serviceFee?.feeAmountGBP;
  const estimatedPayout = soldFor !== undefined && feeAmountGBP !== undefined
    ? soldFor - feeAmountGBP
    : undefined;

  return (
    <div className={styles.wrapper} data-testid="service-fee-summary">
      {withVehicleCard
        && (<div className={styles.vehicleCardWrapper}>
          <VehicleCard
            {...{ badge, image, showVehicleInfo, vehicleDetails }}
            vrm={vrmPretty}
          />
        </div>)
      }
      <section className={styles.breakdown}>
        {
          soldFor
          && (
            <div className={styles.row}>
              <span>{LocalT.translate('soldFor') as string}</span>
              <span data-testid="service-fee-summary-sold-for">
                {`£${soldFor.toLocaleString()}`}
              </span>
            </div>)
        }
        {
          feeAmountGBP
          && (<div className={styles.row}>
            <span>
              <ServiceFeeAppliesNotice
                label={LocalT.translate('serviceFeeLabel') as string}
                serviceFeeTiers={serviceFeeTiers ?? undefined}
              />
            </span>
            <span data-testid="service-fee-summary-fee">
              {`-£${feeAmountGBP.toLocaleString()}`}
            </span>
          </div>)
        }
        <div className={styles.divider} />
        { soldFor && (
          <div className={styles.row}>
            <span className={styles.labelBold}>{LocalT.translate('estimatedPayout') as string}</span>
            <span className={styles.valueBold} data-testid="service-fee-summary-payout">
              {estimatedPayout ? `£${estimatedPayout.toLocaleString()}` : `£${soldFor.toLocaleString()}`}
            </span>
          </div>)}
        <p data-thc-style-override className={styles.financeDisclaimer} data-testid="service-fee-summary-finance-disclaimer">
          {LocalT.translate('disclaimer') as string}
        </p>
      </section>
    </div>
  );
};
